import React, { useEffect, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Hero from "src/components/Hero/Hero";
import DrawCard from "src/components/PromoDrawsCard/DrawCard";
import TitleSection from "src/components/TitleSection/TitleSection";
import {
  getPromoDrawsScheduleDates,
  shouldPublish,
} from "src/utils/publishUtils";
import {
  getPromotionData,
  sortPromotionsByDisplayOrder
} from "src/utils/promoUtils";
import {
  processMultiDrawPromotions,
  buildPromoData
} from "src/utils/multiDrawUtils";
import styled from "styled-components";
import { DateTime } from "luxon";
import { darkCardStyle } from "src/GlobalStyle";
import { useAccount } from "src/context/account/account.provider";
import Markdown from "src/components/Markdown";
import promoDrawsScheduleConfig from "src/config/promoDrawsScheduleConfig";
import Slider from "react-slick";
import { useWindowWidth } from "src/utils/windowUtils";
import { HiArrowLeftEndOnRectangle, HiArrowRightEndOnRectangle } from "react-icons/hi2";

const now = DateTime.now();
const MAX_CARD_WIDTH = 500; // Maximum width for each card in pixels
const CARD_SPACING = 60; // Approximate spacing between cards (padding + margins)

// Custom arrow components matching VideoCarousel style
const NextArrow = ({ onClick, className }) => (
  <ArrowButton
    type="button"
    onClick={onClick}
    aria-label="Next"
    className={`slick-next-custom ${className || ''}`}
    disabled={className?.includes('slick-disabled')}
  >
    <HiArrowRightEndOnRectangle />
  </ArrowButton>
);

const PrevArrow = ({ onClick, className }) => (
  <ArrowButton
    type="button"
    onClick={onClick}
    aria-label="Previous"
    className={`slick-prev-custom ${className || ''}`}
    disabled={className?.includes('slick-disabled')}
  >
    <HiArrowLeftEndOnRectangle />
  </ArrowButton>
);

const PromoDrawsPage = () => {
  const { t, i18n } = useTranslation();
  const { electronicEntriesSummary, electronicEntriesSummaryFn } = useAccount();
  const [loading, setLoading] = useState(true);
  const windowWidth = useWindowWidth();

  useEffect(() => {
    const fetchPromoDraws = async () => {
      try{
        // Get the published promo draws from the config (both poker and EGD)
        const publishedPromoDraws = Object.entries(promoDrawsScheduleConfig)
          .filter(([_, config]) => shouldPublish(config))
          .map(([promotionId, config]) => ({
            promotionId,
            key: config.key,
            drawType: config.drawType,
            isMultiDraw: config.isMultiDraw,
            subDraws: config.subDraws
          }));

        // Extract all promotion IDs with their draw types
        const allPromotionIds = publishedPromoDraws.flatMap(promo => {
          const ids = promo.isMultiDraw && promo.subDraws
            ? promo.subDraws.map(subDraw => subDraw.promotionId)
            : [promo.promotionId];

          return ids.map(id => ({ id, drawType: promo.drawType }));
        });

        // Separate by draw type and uppercase all IDs
        const pokerPromotionIds = allPromotionIds
          .filter(item => item.drawType === 'poker')
          .map(item => item.id.toUpperCase());

        const egdPromotionIds = allPromotionIds
          .filter(item => item.drawType === 'egd')
          .map(item => item.id.toUpperCase());

        // Call API with all three parameter types for smooth transition
        if (pokerPromotionIds.length > 0 || egdPromotionIds.length > 0) {
          await electronicEntriesSummaryFn({
            promotionIds: pokerPromotionIds, // Current format (backward compatibility)
            poker_ids: pokerPromotionIds,     // New format for poker draws
            egd_ids: egdPromotionIds          // New format for EGD draws
          });
        } else {
          console.log("No published promo draws found");
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchPromoDraws();
  }, [i18n.language]);

  useEffect(() => {
    if (
      Array.isArray(electronicEntriesSummary) ||
      electronicEntriesSummary?.type === "error"
    ) {
      setLoading(false);
    }
  }, [electronicEntriesSummary]);

  return (
    <DrawsSection>
      <Hero
        mainHeroText={t("promoDrawsPage.mainHeroText")}
        description={t("promoDrawsPage.heroDescription")}
        bgImage={"about/about-us-hero-bg_v2.jpg"}
        darkenBg={true}
        className="mb-5"
      />
      <container-fluid>
        {/* <TitleSection
          dark={false}
          as={"h2"}
          title={t("promoDrawsPage.contentTitle")}
          text={t("promoDrawsPage.contentDescription")}
        /> */}
        {loading ? (
          <EmptyCard className="p-3 mx-auto">
            <p className="pb-3">{t("promoDrawsPage.loadingPromos")}</p>
            <Spinner animation="border" variant="light" />
          </EmptyCard>
        ) : electronicEntriesSummary?.type === "error" ? (
          <Row xs={1} sm={1} md={2} lg={3}>
            <CardCol>
              <DrawCard
                error={electronicEntriesSummary}
              />
            </CardCol>
          </Row>
        ) : Array.isArray(electronicEntriesSummary) && electronicEntriesSummary.length > 0 ? (
          (() => {
            const currentData = electronicEntriesSummary;

            // Process promotions to handle multi-draw logic
            const processedPromotions = processMultiDrawPromotions(
              currentData,
              promoDrawsScheduleConfig,
              shouldPublish
            );

            const sortedDraws = sortPromotionsByDisplayOrder(processedPromotions.map(p => p.draw));
            const drawCount = sortedDraws.length;

            // Render cards based on count
            const drawCards = sortedDraws
              .map((draw) => {
                const processed = processedPromotions.find(p => p.draw.promotion_id === draw.promotion_id);
                if (!processed) return null;

                const { config, subDrawData } = processed;
                const promoData = buildPromoData(config, t);

                return (
                  <DrawCard
                    key={draw?.promotion_id}
                    draw={draw}
                    promoData={promoData}
                    subDrawData={subDrawData}
                    mergeTickets={promoData?.mergeTickets}
                  />
                );
              })
              .filter(Boolean);

            // Calculate if screen is wide enough to show all cards side by side
            const totalRequiredWidth = (MAX_CARD_WIDTH * drawCount) + (CARD_SPACING * (drawCount + 1));
            const shouldShowGrid = windowWidth >= totalRequiredWidth;

            // Single entry or exactly 2 cards on wide screen: show grid layout
            // 3+ cards: always show carousel
            if (drawCount === 1 || (drawCount === 2 && shouldShowGrid)) {
              return (
                <GridContainer>
                  {drawCards.map((card, index) => (
                    <CardWrapper key={index}>{card}</CardWrapper>
                  ))}
                </GridContainer>
              );
            }

            // Multiple entries on narrow screen: carousel with peek behavior
            const carouselSettings = {
              centerMode: true,
              centerPadding: "300px",
              dots: false,
              arrows: true,
              infinite: false, // No infinite scroll
              lazyload: true,
              speed: 450,
              waitForAnimate: false,
              swipeToSlide: true,
              touchThreshold: 12,
              slidesToShow: 1,
              slidesToScroll: 1,
              adaptiveHeight: true,
              nextArrow: <NextArrow />,
              prevArrow: <PrevArrow />,
              responsive: [
                {
                  breakpoint: 1100,
                  settings: {
                    centerPadding: "100px",
                  }
                },
                {
                  breakpoint: 768,
                  settings: {
                    centerPadding: "30px",
                  }
                },
                {
                  breakpoint: 480,
                  settings: {
                    centerPadding: "10px",
                  }
                }
              ]
            };

            return (
              <CarouselContainer>
                <Slider {...carouselSettings}>
                  {drawCards}
                </Slider>
              </CarouselContainer>
            );
          })()
        ) : (
          <EmptyCard className="p-3 mx-auto">
            <h3 className="pb-3">{t("promoDrawsPage.noDraws")}</h3>
            <Markdown>{t("promoDrawsPage.noDrawsDescription")}</Markdown>
          </EmptyCard>
        )}
      </container-fluid>
    </DrawsSection>
  );
};

const DrawsSection = styled.section`
  background: black;
  color: white;
  text-align: center;
  padding: 0 0 3rem 0;
  overflow-x: hidden;
  .main-hero-text h1 {
    @media screen and (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const CardCol = styled(Col)`
  margin: 0 auto;
`;

const EmptyCard = styled.div`
  ${darkCardStyle}
  max-width: 500px;
`;

const GridContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 20px;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  flex-wrap: wrap;
`;

const CardWrapper = styled.div`
  flex: 0 0 auto;
  max-width: ${MAX_CARD_WIDTH}px;
  width: 100%;
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;

  .slick-slider {
    .slick-list {
      overflow: visible;
      touch-action: pan-y;
    }

    .slick-track {
      display: flex;
      align-items: stretch;
    }

    .slick-slide {
      padding: 0 10px;
      transition: opacity 0.3s ease;

      &:not(.slick-center) {
        opacity: 0.2;
        transform: scale(0.85);
        transition: opacity 0.3s ease, transform 0.3s ease;
      }

      &.slick-center {
        opacity: 1;
        transform: none;
      }

      & > div {
        height: 100%;
        max-width: ${MAX_CARD_WIDTH}px;
        margin: 0 auto;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .slick-slider .slick-slide:not(.slick-center) {
      transform: scale(0.75);
    }
  }

  @media screen and (max-width: 480px) {
    padding: 0 10px;

    .slick-slider .slick-slide:not(.slick-center) {
      transform: scale(0.7);
    }
  }
`;

const ArrowButton = styled.button`
  border: 0;
  background: rgba(0, 0, 0, 0.8);
  padding: 10px;
  box-sizing: content-box;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  line-height: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  flex: 0 0 auto;
  color: rgba(250, 225, 169, 0.9);
  transition: opacity 0.3s ease, background 0.3s ease, transform 0.3s ease;
  opacity: 0.8;

  /* Hide default slick arrow pseudo-elements */
  &::before {
    display: none !important;
    content: none !important;
  }

  &:hover:not(:disabled) {
    opacity: 1;
    transform: translateY(-50%) scale(1.15);
  }

  &:disabled,
  &.slick-disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }

  &.slick-prev-custom, &.slick-next-custom {
    width: 60px;
    height: 60px;
    &:hover, &:focus {
      color: rgba(250, 225, 169, 0.9);
    }
  }

  &.slick-prev-custom {
    left: -10px;
  }

  &.slick-next-custom {
    right: -10px;
  }

  svg {
    width: 60px;
    height: 60px;
    display: block;
  }

  @media (max-width: 768px) {
    background: transparent;
    padding: 0;

    svg {
      width: 50px;
      height: 50px;
    }
  }

  @media (max-width: 480px) {
    svg {
      width: 40px;
      height: 40px;
    }
  }
`;

export default PromoDrawsPage;
